import { Grid } from '@mui/material'
import { Form, Formik } from 'formik'
import { memo, useRef, useCallback } from 'react'
import { currencyMask } from '../../../utils/masks'
import { metodoPrograma } from '../../../components/api'
import { isEqual } from 'lodash'
import dayjs from 'dayjs'
import * as Yup from 'yup'
import { setError, setSuccess } from '../../../components/alerts'
import { useModal } from '../../../components/Utils/UseModal'
import ModalImprime from './modalImprime'
import PacienteCard from './components/PacienteCard'
import PropostaCard from './components/PropostaCard'
import TerapiasCard from './components/TerapiasCard'
import ProdutosCard from './components/ProdutosCard'
import AdicionaisCard from './components/AdicionaisCard'
import ResumoFinal from './components/ResumoFinal'
import { CheckinProvider, useCheckin } from './components/CheckinContext'

const validationSchema = Yup.object().shape({
  acFormaPagamento: Yup.array()
    .nullable()
    .when(['vlTotal', 'snCobraCheckout'], {
      is: (vlTotal, snCobraCheckout) =>
        Number(vlTotal) > 0 && snCobraCheckout !== 'S',
      then: (schema) =>
        schema
          .min(1, 'Selecione a forma de pagamento')
          .required('Campo obrigatório'),
      otherwise: (schema) => schema.notRequired(),
    }),
  formasPagamento: Yup.array().when(['vlTotal', 'snCobraCheckout'], {
    is: (vlTotal, snCobraCheckout) =>
      Number(vlTotal) > 0 && snCobraCheckout !== 'S',
    then: (schema) =>
      schema
        .of(
          Yup.object({
            vlPago: Yup.number()
              .required('valor é obrigatório')
              .typeError('valor deve ser um número')
              .moreThan(0, 'O valor deve ser maior que 0'),
            qtParcelas: Yup.number().when('isParcelas', {
              is: true,
              then: (schema) =>
                schema
                  .moreThan(0, 'A quantidade de parcelas deve ser maior que 0')
                  .required('Quantidade de parcelas é obrigatória')
                  .typeError('Quantidade de parcelas deve ser um número'),
              otherwise: (schema) => schema.notRequired(),
            }),
          }),
        )
        .test(
          'formasPagamento-valido',
          'Cada item deve conter vlPago válido',
          function (value) {
            if (!value || value.length === 0) return true

            const { isParcelas } = this.options.context

            const itemIndex = value.findIndex(
              (item) => item.vlPago === undefined || item.vlPago === null,
            )

            if (itemIndex !== -1) {
              return this.createError({
                path: `formasPagamento[${itemIndex}].vlPago`,
                message: `Item ${itemIndex + 1}: Valor é obrigatório`,
              })
            }

            if (isParcelas) {
              const parcelaIndex = value.findIndex(
                (item) =>
                  item.qtParcelas === undefined ||
                  item.qtParcelas === null ||
                  item.qtParcelas <= 0,
              )

              if (parcelaIndex !== -1) {
                return this.createError({
                  path: `formasPagamento[${parcelaIndex}].qtParcelas`,
                  message: `Item ${parcelaIndex + 1}: Quantidade de parcelas é obrigatória`,
                })
              }
            }

            return true
          },
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
})

const EdicaoInner = ({ voltar }) => {
  const formikRef = useRef()

  const {
    listaTerapias,
    listaProdutos,
    listaProdutosAdicionais,
    listaVendas,
    dependentes,
    initialValues,
    getCalculos,
  } = useCheckin()

  const modalImprime = useModal(ModalImprime)

  const imprimir = useCallback(
    async (values, setSubmitting) => {
      setSubmitting(true)

      const calculos = getCalculos(values)
      const resumoEstornos = values.idAtendimento
        ? await metodoPrograma(
            'resumoEstornos',
            { idAtendimento: values.idAtendimento },
            'CtrlOrcamentoCheckin',
          )
        : null
      const totalEstornado = resumoEstornos?.status
        ? Number(resumoEstornos.totalEstornado || 0)
        : 0

      const formatarItem = (item) => ({
        nome: item.nome,
        paciente: item.paciente,
        qtd: item.qtd,
        unitario: `R$ ${currencyMask(item.unitario)}`,
        valor: `R$ ${currencyMask(item.valor)}`,
      })

      await modalImprime({
        proposta: values.idAtendimento ? String(values.idAtendimento) : '',
        contratante: values.dsPaciente || '',
        suite: values.dsQuarto,
        checkIn: values.dtInicio
          ? dayjs(values.dtInicio).format('DD/MM/YYYY')
          : '',
        checkOut: values.dtFim ? dayjs(values.dtFim).format('DD/MM/YYYY') : '',
        terapias: calculos.listaTerapiasTratado
          .filter((item) => item.qtd > 0)
          .map(formatarItem),
        suplementos: calculos.listaProdutosTratado
          .filter((item) => item.qtd > 0)
          .map(formatarItem),
        adicionais: calculos.listaProdutosAddTratado
          .filter((item) => item.qtd > 0)
          .map(formatarItem),
        vendas: calculos.listaVendasTratado
          .filter((item) => item.qtd > 0)
          .map(formatarItem),
        dependentes,
        subTotal: `R$ ${currencyMask(calculos.saldoProposta || 0)}`,
        caucao: `R$ ${currencyMask(values.vlCaucao || 0)}`,
        desconto: `R$ ${currencyMask(values.vlDesconto || 0)}`,
        formasPagamento: (values?.acFormaPagamento || []).map((item, i) => ({
          nome: item?.[1] || '',
          vlPago: values?.formasPagamento?.[i]?.vlPago ?? item?.[2] ?? 0,
        })),
        totalEstornado,
        total: `R$ ${currencyMask(calculos.totalGeral || 0)}`,
      })
      setSubmitting(false)
    },
    [modalImprime, dependentes, getCalculos],
  )

  const montarPayload = useCallback(
    (values) => {
      const { totalGeral } = getCalculos(values)

      return {
        ...values,
        acFormaPagamento:
          totalGeral > 0 && values.snCobraCheckout !== 'S'
            ? values?.acFormaPagamento
            : [],
        formasPagamento:
          totalGeral > 0 && values.snCobraCheckout !== 'S'
            ? values?.formasPagamento
            : [],
        itens: [...listaProdutos, ...listaTerapias],
        itensAdicionais: [...listaProdutosAdicionais, ...listaVendas].filter(
          (item) => item?.qtProduto > 0,
        ),
        vlTotal: totalGeral,
      }
    },
    [
      getCalculos,
      listaProdutos,
      listaTerapias,
      listaProdutosAdicionais,
      listaVendas,
    ],
  )

  const salvarRascunho = useCallback(
    async (values, setSubmitting) => {
      setSubmitting(true)
      const res = await metodoPrograma(
        'salvaRascunho',
        montarPayload(values),
        'CtrlOrcamentoCheckin',
      )
      if (res.status) {
        setSuccess('Rascunho salvo')
        return voltar()
      }
      setSubmitting(false)
      setError(res?.dsErro || 'Erro ao salvar o rascunho!')
    },
    [montarPayload, voltar],
  )

  const excluirRascunho = useCallback(async () => {
    if (!window.confirm('Deseja excluir este rascunho?')) return
    const res = await metodoPrograma(
      'excluiRascunho',
      { idAtendimento: initialValues.idAtendimento },
      'CtrlOrcamentoCheckin',
    )
    if (res.status) {
      setSuccess('Rascunho excluído')
      return voltar()
    }
    setError(res?.dsErro || 'Erro ao excluir o rascunho!')
  }, [initialValues.idAtendimento, voltar])

  const inserePedidoVenda = useCallback(
    async (values, { setSubmitting }) => {
      const { totalGeral } = getCalculos(values)
      const totalPago = (values?.formasPagamento || []).reduce(
        (acumulador, formaPagamento) =>
          acumulador + Number(formaPagamento?.vlPago || 0),
        0,
      )

      if (
        values.snCobraCheckout !== 'S' &&
        totalGeral > 0 &&
        Math.abs(totalPago - totalGeral) >= 0.01
      ) {
        setSubmitting(false)
        return setError('O valor pago deve ser igual ao Total a Pagar')
      }

      const res = await metodoPrograma(
        'salvaPedido',
        montarPayload(values),
        'CtrlOrcamentoCheckin',
      )
      if (res.status) {
        setSuccess('Pedido Registrado')
        return voltar()
      } else {
        setSubmitting(false)
        setError(res?.dsErro || 'Erro ao salvar!')
      }
    },
    [getCalculos, montarPayload, voltar],
  )

  console.log('initialValues', initialValues)

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={initialValues}
      innerRef={formikRef}
      onSubmit={inserePedidoVenda}
    >
      {() => (
        <Form
          style={{
            width: 'calc(100% + 20px)',
            height: 'calc(100vh - 140px)',
            display: 'flex',
            flexDirection: 'column',
            margin: '-10px',
          }}
        >
          <Grid
            container
            direction="column"
            wrap="nowrap"
            sx={{
              flex: 1,
              overflow: 'auto',
              bgcolor: 'grey.100',
              p: 2,
              gap: 2,
            }}
          >
            <Grid item>
              <PacienteCard />
            </Grid>
            <Grid item>
              <PropostaCard preencherAltura />
            </Grid>
            <Grid item>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TerapiasCard preencherAltura />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ProdutosCard preencherAltura />
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                <AdicionaisCard preencherAltura />
              </Grid>
              <Grid item xs={12} md={4}>
                <Grid container spacing={2} direction="column">
                  <Grid item>
                    <ResumoFinal
                      onImprimir={imprimir}
                      onSalvarRascunho={salvarRascunho}
                      onExcluirRascunho={excluirRascunho}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

const Edicao = ({
  dados: { linha, itens, isFinalizado, isRascunho, somenteLeitura },
  voltar,
  pontoVenda,
}) => (
  <CheckinProvider
    linha={linha}
    itens={itens}
    isFinalizado={isFinalizado}
    isRascunho={isRascunho}
    somenteLeitura={somenteLeitura}
    pontoVenda={pontoVenda}
  >
    <EdicaoInner voltar={voltar} />
  </CheckinProvider>
)

export default memo(Edicao, isEqual)
